/**
 * Validierung der Stellungnahme-Eingabe.
 *
 * Implementiert die Regeln aus `docs/Platzhalter_Referenz.md` §
 * "Validierungsregeln für die Generierung". Wenn eine Regel verletzt ist,
 * sollte die UI den Generator-Aufruf BLOCKIEREN — nicht die Stellungnahme
 * mit Lücken erzeugen.
 */

import type { BuildPlaceholdersInput } from './types.js';

export interface ValidationIssue {
  readonly code: string;
  readonly message: string;
  readonly severity: 'error' | 'warning';
}

const REQUIRED_ASSESSMENT_FIELDS = [
  'verdictLongParagraph',
  'verdictReasoning',
  'recommendationDetailed',
  'constraintDiscussion',
  'riskIndicators',
  'strengthIndicators',
] as const;

export function validateStatementInput(input: BuildPlaceholdersInput): ValidationIssue[] {
  const issues: ValidationIssue[] = [];

  // 1. Pflichtfelder bei B2G: Jobcenter-Daten
  if (input.caseData.type === 'b2g') {
    if (!input.recipient.jobcenterName) {
      issues.push({
        code: 'B2G_JC_NAME_MISSING',
        message: 'Jobcenter-Name ist Pflicht bei B2G-Stellungnahmen.',
        severity: 'error',
      });
    }
    if (!input.recipient.jobcenterFileNumber) {
      issues.push({
        code: 'B2G_JC_FILE_NUMBER_MISSING',
        message: 'Jobcenter-Aktenzeichen ist Pflicht bei B2G-Stellungnahmen.',
        severity: 'error',
      });
    }
  }

  // 2. Pflicht-Bewertungs-Texte
  for (const field of REQUIRED_ASSESSMENT_FIELDS) {
    const value = input.assessments[field];
    if (typeof value !== 'string' || value.trim().length === 0) {
      issues.push({
        code: 'ASSESSMENT_FIELD_EMPTY',
        message: `Bewertungs-Feld "${field}" ist Pflicht und darf nicht leer sein.`,
        severity: 'error',
      });
    }
  }

  // 3. Score-Konsistenz
  const total = input.score.totalScore as number;
  const traffic = input.score.trafficLight;
  const expectedTraffic = total >= 70 ? 'green' : total >= 40 ? 'yellow' : 'red';
  if (traffic !== expectedTraffic) {
    issues.push({
      code: 'SCORE_TRAFFIC_INCONSISTENT',
      message: `Score ${String(total)} passt nicht zur Ampelfarbe "${traffic}" (erwartet "${expectedTraffic}").`,
      severity: 'error',
    });
  }

  // 4. Konfidenz-Schwelle
  const confidence = input.score.totalConfidencePercent as number;
  if (confidence < 60) {
    issues.push({
      code: 'CONFIDENCE_LOW',
      message: `Konfidenz von ${String(confidence)}% liegt unter 60% – Stellungnahme nur als „nicht abschließend" generieren.`,
      severity: 'warning',
    });
  }

  // 5. Score-State: nicht final
  if (input.score.state !== 'final') {
    issues.push({
      code: 'SCORE_NOT_FINAL',
      message: `Score-State ist "${input.score.state}". Eine finale Stellungnahme verlangt vollständige Daten in allen 6 Dimensionen.`,
      severity: 'warning',
    });
  }

  // 6. Datum-Plausibilität — nur prüfbar, wenn die optionalen Termine erfasst
  // sind. Fehlen sie, weist das Gutachten „nicht erfasst" aus (kein Fehler).
  const statementDate = input.meeting.statementDate.getTime();
  const meetingDate = input.meeting.meetingDate?.getTime();
  const dataDate = input.meeting.dataSubmissionDate?.getTime();
  if (meetingDate !== undefined && statementDate < meetingDate) {
    issues.push({
      code: 'DATE_STATEMENT_BEFORE_MEETING',
      message: 'Datum der Stellungnahme liegt vor dem Termin. Bitte Daten korrigieren.',
      severity: 'error',
    });
  }
  if (meetingDate !== undefined && dataDate !== undefined && meetingDate < dataDate) {
    issues.push({
      code: 'DATE_MEETING_BEFORE_SUBMISSION',
      message: 'Termin-Datum liegt vor der Daten-Einreichung. Bitte Daten korrigieren.',
      severity: 'error',
    });
  }

  return issues;
}

/** Convenience: nur Errors zurück, kein Warning. */
export function getBlockingIssues(issues: ValidationIssue[]): ValidationIssue[] {
  return issues.filter((i) => i.severity === 'error');
}
